/* global com */
define((require) => {
    "use strict";

    const Marionette    = require("marionette");
    const TweenMax      = require("TweenMax");
    const EasingTypes   = require("shared/ui/experience/widgets/animations/EasingTypes");
    const Browser       = require("shared/services/Browser");
    const Preconditions = require("shared/util/Preconditions");
    const _             = require("underscore");

    // Import/include TweenMax dependencies
    require("TweenCSSPlugin");
    require("TweenEasePlugin");

    /**
     * Describes the different kinds of animations.
     * @see  #type
     * @type {Object}
     */
    const Type = {
        STANDARD: "standard",
        INTRO: "intro",
        OUTRO: "outro"
    };

    /**
     * Returns the (TweenMax) easing object given the html valid name.
     *
     * @param {String} modelValue The htmlValidName of the easing type.
     * @returns {Object} the (TweenMax) easing object given the html valid name.
     */
    function toGSAPEasingType(modelValue) {
        const {easing} = com.greensock;
        let value;

        switch (modelValue) {
            case EasingTypes.LINEAR.htmlValidName:
                value = easing.Linear.easeIn;
                break;

            case EasingTypes.EASE_IN.htmlValidName:
                value = easing.Quad.easeIn;
                break;

            case EasingTypes.EASE_OUT.htmlValidName:
                value = easing.Quad.easeOut;
                break;

            case EasingTypes.EASE_IN_OUT.htmlValidName:
                value = easing.Quad.easeInOut;
                break;

            case EasingTypes.BOUNCE.htmlValidName:
                value = easing.Bounce.easeOut;
                break;

            case EasingTypes.ELASTIC.htmlValidName:
                value = easing.Elastic.easeOut;
                break;

            case EasingTypes.BACK_OUT.htmlValidName:
                value = easing.Back.easeOut;
                break;
            default:
                value = easing.Linear.easeIn;
                break;
        }

        return value;
    }

    /**
     * The abstract animation controller.
     *
     * @class AnimationController
     * @constructor
     *
     * @param {AnimationModel} animationModel The animation model.
     * @param {AbstractWidgetView} widgetView The widget to animate.
     * @param {Object} [unscaledExperienceSize] The size of the parent experience, e.g. {width: 800, height: 600}
     * @param {Boolean} [immediateRender] Whether potential "from" values should be rendered/applied
     * immediately. See http://api.greensock.com/js/ for more info.
     */
    return Marionette.Controller.extend({

        /**
         * The animation type, defaulting to <tt>Type.STANDARD</tt>. E.g. for animations fading out or
         * sliding out, the type would be <tt>Type.OUTRO</tt>.
         *
         * @type {String}
         */
        type: Type.STANDARD,

        /**
         * Internal state for whether the animation has been started.
         * @type {Boolean}
         */
        _started: false,

        /**
         * Stores the timeout ID returned by setTimeout - used for managing the delay, if any.
         *
         * @type {Number}
         */
        _timeoutID: null,

        constructor(options) {
            this.__super();

            this.animationModel = options.animationModel;
            this.widgetView = options.widgetView;
            this.widgetModel = options.widgetView.model;
            this.element = options.widgetView.el;
            this.elementRect = options.widgetView.getRect();
            this.elementBounds = options.widgetView.getBoundingBox();
            this.unscaledExperienceSize = options.unscaledExperienceSize;
            this.immediateRender = options.immediateRender;
            // We need the duration in seconds
            this.duration = options.animationModel.get("duration") / 1000;
            this.delay = options.animationModel.get("delay");
            this.easing = toGSAPEasingType(options.animationModel.get("easing"));
            this.positionOffset = this.options.positionOffset;

            this.init();
        },

        _getTweenLiteMethodName(animationDescriptor) {
            for (const p in animationDescriptor) {
                if (animationDescriptor.hasOwnProperty(p)) {
                    return p;
                }
            }

            return null;
        },

        _getTweenDescriptor() {
            const animationDescriptor = this.descriptor();
            const methodName = this._getTweenLiteMethodName(animationDescriptor);
            const animationDescriptorValue = animationDescriptor[methodName];
            const tweenFunction = TweenMax[methodName];
            const descriptor = {
                tweenFunction,
                vars: animationDescriptorValue,
                immediateRender: this.immediateRender && methodName !== "to"
            };

            // roundProps to force transform: translate3d to integer values
            const roundProps = {roundProps: "x,y"};

            if (methodName === "fromTo") {
                // Expecting 'animationDescriptorValue' to be an array of 'fromVars' and 'toVars'
                descriptor.from = _.extend(animationDescriptorValue[0], roundProps);
                descriptor.to = _.extend(animationDescriptorValue[1], roundProps);
            } else {
                descriptor.to = _.extend(animationDescriptorValue, roundProps);
            }

            return descriptor;
        },

        _disposeAnimation() {
            if (this.animation) {
                this.animation.kill();
                this.animation = null;
            }
        },

        init() {
            // Prepare descriptor for the TweenMax API
            const animationDescriptor = this._getTweenDescriptor();
            const defaultDescriptor = {
                force3D: Browser.capabilities.is3dAnimationsSupported, /* see http://api.greensock.com/js/ */
                paused: true,
                ease: this.easing
            };
            const tweenFunction = animationDescriptor.tweenFunction;
            const tweenLiteDescriptor = _.extend(defaultDescriptor, animationDescriptor.to);

            // Method signature is different when using "fromTo" - see http://api.greensock.com/js/
            if (animationDescriptor.from) {
                const {x, y} = this.elementRect;
                // For some reason it seems we need to preserve rotation, x and y when using 'fromTo'?
                _.defaults(animationDescriptor.from, {rotation: this.widgetModel.get("rotation"), x, y});

                // Even though we set 'immediateRender' to true, we still experience a brief flicker of
                // the end state on some devices when using 'fromTo' (MOS-1650)
                if (tweenLiteDescriptor.immediateRender) {
                    TweenMax.set(this.element, animationDescriptor.from);
                }

                this.animation = tweenFunction(this.element, this.duration, animationDescriptor.from, tweenLiteDescriptor);
            } else {
                // Create the animation instance
                this.animation = tweenFunction(this.element, this.duration, tweenLiteDescriptor);
            }
        },

        /**
         * Recalculates and updates target tween values from the current state of the
         * associated widget view (AbstractWidgetView#getRect).
         *
         * @see  _getTweenDescriptor
         */
        invalidate() {
            this.elementRect = this.widgetView.getRect();
            this.elementBounds = this.widgetView.getBoundingBox();

            const animationDescriptor = this._getTweenDescriptor();
            const {x, y} = this.elementRect;
            const params = {
                css: animationDescriptor.to,
                startAt: animationDescriptor.from || {x, y}
            };

            // http://greensock.com/docs/#/HTML5/GSAP/TweenMax/updateTo/
            this.animation.invalidate().updateTo(params);
        },

        /**
         * Describes the animation.
         *
         * This is an abstract method that must be overridden by it's subclass.
         *
         * @return {Object} Object describing the animation, in the from:<p>
         *
         * <tt>{from: {opacity: 0}}</tt>
         */
        descriptor() {
            Preconditions.abstractMethodError();
        },

        /**
         * Calculates the size and offset of any "styles" applied to the assosiated widget.
         *
         * @return {Object} An object representing the size and position of the styling element.
         */
        calculateStylingRect() {
            const size = {
                x: 0,
                y: 0,
                width: 0,
                height: 0
            };

            if (this.widgetModel.has("boxShadow")) {
                const boxShadow     = this.widgetModel.get("boxShadow").match(/(-?\d+px)/g);
                const shadowOffsetY = parseInt(boxShadow[0], 10);
                const shadowOffsetX = parseInt(boxShadow[1], 10);
                const shadowBlur    = parseInt(boxShadow[2], 10);
                const shadowSpread  = parseInt(boxShadow[3], 10);

                size.width = size.height = shadowBlur + shadowSpread;
                size.x = shadowOffsetX;
                size.y = shadowOffsetY;
            }

            return size;
        },

        /**
         * Returns a descriptor object describing the current appearance
         * (i.e. position and pivot point) of the widget.
         *
         * @return {Object} a descriptor object describing the current appearance
         * (i.e. position and pivot point) of the widget.
         */
        getAppearanceDescriptor() {
            return {
                scale: this.widgetModel.get("scale"),

                styling: this.calculateStylingRect(),

                position: {
                    x: this.elementRect.x,
                    y: this.elementRect.y
                },

                pivotOffset: {
                    width: (this.elementBounds.width - this.elementRect.width) / 2,
                    height: (this.elementBounds.height - this.elementRect.height) / 2
                },

                positionOffset: {
                    x: this.positionOffset.left,
                    y: this.positionOffset.top
                }
            };
        },

        /**
         * Starts the animation.
         */
        start() {
            if (this._started) {
                return;
            }

            this._started = true;
            // Start the animation in 'this.delay' milliseconds
            this._timeoutID = setTimeout(this.animation.play.bind(this.animation), this.delay);
        },

        /**
         * Stops the animation.
         */
        stop() {
            if (!this._started) {
                return;
            }

            this._started = false;

            // Clear the timeout callback
            clearTimeout(this._timeoutID);

            // Reset the animation in a paused state
            this.animation.paused(true).totalTime(0);
        },

        paused(value) {
            return this.animation.paused(value);
        },

        /**
         * Indicates whether or not the animation is currently
         * active (meaning the virtual playhead is actively moving across this
         * instance's time span and it is not paused, nor are any of its
         * ancestor timelines).
         *
         * @return {Boolean} Whether or not the animation is currently active.
         */
        isActive() {
            return this.animation.isActive();
        },

        /**
         * Whether the animation has completed.
         *
         * @return {Boolean} <tt>true</tt> if the animation has completed, <tt>false</tt> if not.
         */
        isComplete() {
            return this.animation.time() === this.animation.totalDuration();
        },

        /**
         * Whether the animation is running (has been started), regardless of any delays.
         *
         * @return {Boolean} <tt>true</tt> if the animation has been started and is not completed yet.
         */
        isRunning() {
            return this._started && !this.isComplete();
        },

        onClose() {
            this._disposeAnimation();
        }
    }, {
        Type
    });
});



// WEBPACK FOOTER //
// ./src/shared/ui/experience/widgets/animations/AnimationController.js